import React, { useState, useEffect, useContext } from 'react';
import { withRouter, Route, Switch, Redirect } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import GenericListPair from 'modules/common/components/GenericListPair';
import ConfirmAlert from 'modules/common/components/ConfirmAlert';
import useDebounce from 'modules/common/components/UseDebounce';
import { ModalContext } from 'App';
import PropTypes from 'prop-types';
import * as actions from '../action';
import * as c from '../constant';
import CreateModal from '../components/CreateModal';
import UpdateModal from '../components/UpdateModal';
import NewBusinessNaturesContent from './NewBusinessNaturesContent';

const form = {
  keyword: '',
};

function NewBusinessNaturesContainer({
  match,
  history,
  getNewBusinessNaturesList,
  newBusinessNaturesList,
  isLoading,
  removeNewBusinessNatures,
  newBusinessNaturesListPager,
  getSingleNewBusinessNatures,
  ordinanceRevisionId,
}) {
  const [formData, setFormData] = useState(form);
  const setModal = useContext(ModalContext);

  const handleOnChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAddModal = e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: <CreateModal ordinanceRevisionId={ordinanceRevisionId} />,
      title: 'Add Business Nature',
      modalSize: 'modal-md',
    });
  };

  const handleEdit = data => e => {
    e.preventDefault();
    setModal({
      isOpen: true,
      content: (
        <UpdateModal
          updateList={data.attributes}
          id={data.id}
          newBusinessNaturesInfo={data}
          ordinanceRevisionId={ordinanceRevisionId}
        />
      ),
      title: 'Update Business Nature',
      modalSize: 'modal-md',
    });
  };

  const handleRemove = data => e => {
    e.preventDefault();
    ConfirmAlert(
      'Are you sure, you want to remove this business?',
      () => {
        removeNewBusinessNatures(data.id, () => {
          setFormData({
            ...formData,
            keyword: '',
          });
          getNewBusinessNaturesList();
          const id = _.get(newBusinessNaturesList, '1.id');
          history.push(`/bpls/new-business/new-business-natures/${id}`);
          getSingleNewBusinessNatures(id);
        });
      },
      () => {},
      {
        yesLabel: 'Confirm',
        noLabel: 'Cancel',
      }
    );
  };

  useEffect(() => {
    getNewBusinessNaturesList();
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  const debouncedSearchTerm = useDebounce(formData, 500);

  const param = {
    q: formData.keyword,
    sort_by: 'asc',
  };

  useEffect(() => {
    if (debouncedSearchTerm && ordinanceRevisionId) {
      getNewBusinessNaturesList({
        ordinance_revision_id: ordinanceRevisionId,
        q: formData.keyword,
        sort_by: 'desc',
      });
    } else {
      setFormData([]);
    }
  }, [debouncedSearchTerm, ordinanceRevisionId]);

  return (
    <div className="card-content-listing">
      <Route
        path={`${match.path}/:newBusinessNaturesId?`}
        render={({ match: { params } }) => (
          <GenericListPair
            moduleName="Business Natures"
            getData={getNewBusinessNaturesList}
            setParams={param}
            setKeyword={handleOnChange}
            keyword={_.get(formData, 'keyword') || ''}
            keywordPlaceholder="Search..."
            pathName={`${match.path}/:newBusinessNaturesId?`}
            listName="newBusinessNatures"
            label="Business Natures"
            renderItem={item => (
              <div className="p-3">{_.get(item, 'attributes.name')}</div>
            )}
            isLoading={isLoading}
            selected={params.newBusinessNaturesId}
            data={newBusinessNaturesList}
            dataPager={newBusinessNaturesListPager}
            handleSelectRow={data => {
              history.push(`${match.url}/${_.get(data, 'id')}`);
            }}
            renderContent={() => (
              <React.Fragment>
                <Switch>
                  <Route
                    path={`${match.path}/:newBusinessNaturesId?`}
                    render={() => (
                      <NewBusinessNaturesContent
                        ordinanceRevisionId={ordinanceRevisionId}
                        handleAddModal={handleAddModal}
                        handleEdit={handleEdit}
                        handleRemove={handleRemove}
                      />
                    )}
                  />
                </Switch>
                <Route
                  path={`${match.path}/:newBusinessNaturesId?`}
                  render={() =>
                    !params.business_Id && newBusinessNaturesList.length > 0 ? (
                      <Redirect
                        to={`${match.url}/${_.get(
                          newBusinessNaturesList,
                          '0.id'
                        )}`}
                      />
                    ) : null
                  }
                />
              </React.Fragment>
            )}
          />
        )}
      />
    </div>
  );
}

NewBusinessNaturesContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
  history: PropTypes.instanceOf(Object).isRequired,
  getNewBusinessNaturesList: PropTypes.instanceOf(Function).isRequired,
  newBusinessNaturesList: PropTypes.instanceOf(Array).isRequired,
  newBusinessNaturesListPager: PropTypes.instanceOf(Object).isRequired,
  removeNewBusinessNatures: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  getSingleNewBusinessNatures: PropTypes.instanceOf(Function).isRequired,
  ordinanceRevisionId: PropTypes.number.isRequired,
};

const mapStateToProps = ({ api }) => ({
  newBusinessNaturesList:
    _.get(api, `${c.GET_NEW_BUSINESS_NATURES}.list`) || [],
  newBusinessNaturesListPager:
    _.get(api, `${c.GET_NEW_BUSINESS_NATURES}.pager`) || {},
  isLoading: _.get(api, 'loading', []).indexOf(c.GET_NEW_BUSINESS_NATURES) > -1,
});

const enhance = _.flowRight([withRouter, connect(mapStateToProps, actions)]);

export default enhance(NewBusinessNaturesContainer);
